import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Archive, ArrowLeft, BookOpen, ChevronRight, ClipboardList, Clock, DoorOpen, Pencil, Phone, Search, ShieldCheck, UserPlus, Users,
  WifiOff, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAgent } from '../App';
import { desktopApi } from '../lib/desktopApi';
import { searchConsignes, searchStudents } from '../lib/consignesSearch.mjs';
import {
  readConsignesCache, readStudentsCache, writeConsignesCache, writeStudentsCache,
} from '../lib/consignesCache';
import ConsignesMarkdown, { Highlight, articleTitles } from '../components/ConsignesMarkdown';
import { formatStamp } from '../lib/time';
import FollowupNotes from '../components/FollowupNotes';
import WarningBadge from '../components/WarningBadge';
import StudentForm from '../components/StudentForm';
import {
  studentEditBlocked, studentEditError, studentFormError, studentPayload,
} from '../lib/studentEdit.mjs';
import { WARNING_LEVELS } from '../lib/warningLevels';

const CARD_INFO = {
  'aide-memoire': { icon: Clock, hint: '22h00, 23h00, 05h00, 05h30, 06h00, 06h15…' },
  checkin: { icon: DoorOpen, hint: 'Bulletin, pièce d\'identité, caution, taxe de séjour', highlight: true },
  protocole: { icon: ClipboardList, hint: 'Contrôles A à P, rondes, matériel' },
  reglement: { icon: BookOpen, hint: 'Aide-mémoire et articles 1 à 19' },
  mission: { icon: ShieldCheck, hint: 'Mission, tenue, règles d\'or' },
  contacts: { icon: Phone, hint: 'Appeler ou écrire d\'un geste' },
};
const STUDENTS_ID = 'etudiants';

const inputClass = 'h-14 w-full rounded-lg border border-input bg-card pl-12 pr-12 text-[17px] focus:outline-none focus:ring-2 focus:ring-ring';

function stamp(value) {
  if (!value) return '';
  try {
    return formatStamp(value);
  } catch (error) {
    return '';
  }
}

const SearchField = ({ value, onChange, placeholder, testId, label }) => (
  <div className="relative">
    <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
    <input
      type="search"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      aria-label={label}
      data-testid={testId}
      enterKeyHint="search"
      autoComplete="off"
      className={inputClass}
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        aria-label="Effacer la recherche"
        className="absolute right-1 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
      >
        <X className="h-5 w-5" />
      </button>
    )}
  </div>
);

const StudentRow = ({ student, query, onOpen }) => {
  const details = [
    student.class_name ? `Classe ${student.class_name}` : '',
    student.departure ? `Départ ${student.departure}` : '',
    student.note_count ? `${student.note_count} note(s)` : '',
  ].filter(Boolean).join(' · ');
  return (
    <li data-testid="student-row">
      <button
        type="button"
        onClick={() => onOpen?.(student)}
        className="flex min-h-16 w-full items-center gap-4 rounded-lg border border-border bg-card px-4 py-3 text-left hover:border-primary/60"
      >
        <span className="flex h-12 min-w-16 shrink-0 items-center justify-center rounded-md bg-primary/15 px-2 text-xl font-black text-primary">
          <Highlight text={student.room} query={query} />
          {student.bed ? <span className="ml-1 text-sm">{student.bed}</span> : null}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[17px] font-bold leading-tight">
            <span className="uppercase"><Highlight text={student.last_name} query={query} /></span>{' '}
            <Highlight text={student.first_name} query={query} />
          </span>
          {details && <span className="mt-0.5 block text-sm text-muted-foreground">{details}</span>}
        </span>
        {student.warning_level ? <WarningBadge level={student.warning_level} /> : null}
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
      </button>
    </li>
  );
};

const Legend = () => (
  <details className="mb-4 rounded-lg border border-border bg-card" data-testid="warning-legend">
    <summary className="flex min-h-12 cursor-pointer items-center px-4 text-sm font-semibold">Légende des avertissements</summary>
    <ul className="space-y-2 px-4 pb-4">
      {WARNING_LEVELS.map((item) => (
        <li key={item.level} className="flex items-center gap-3 text-sm">
          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-black ${item.badge}`}>{item.level}</span>
          {item.label}
        </li>
      ))}
    </ul>
  </details>
);

const ConsignesPage = () => {
  const navigate = useNavigate();
  const { sectionId } = useParams();
  const { currentAgent } = useAgent();
  const agentId = currentAgent?.id;
  const [data, setData] = useState(() => readConsignesCache());
  const [offline, setOffline] = useState(false);
  const [students, setStudents] = useState(() => readStudentsCache(agentId));
  const [studentsOffline, setStudentsOffline] = useState(false);
  const [query, setQuery] = useState('');
  const [studentQuery, setStudentQuery] = useState('');
  const [openArticles, setOpenArticles] = useState(() => new Set());
  const [focusKey, setFocusKey] = useState('');
  const [sectionQuery, setSectionQuery] = useState('');
  // Sélection gardée en mémoire seulement : aucun nom dans l'adresse de la page.
  const [notesTarget, setNotesTarget] = useState(null);
  // Modification de la liste : '' | 'add' | 'edit' | 'archive'. Jamais hors ligne.
  const [studentEdit, setStudentEdit] = useState('');
  const [studentBusy, setStudentBusy] = useState(false);

  useEffect(() => {
    if (!sectionId) setSectionQuery('');
    if (sectionId !== STUDENTS_ID) setNotesTarget(null);
    setStudentEdit('');
  }, [sectionId]);

  const loadConsignes = useCallback(async () => {
    try {
      const fresh = await desktopApi.getConsignes();
      setData(fresh);
      setOffline(false);
      writeConsignesCache(fresh);
    } catch (error) {
      setOffline(true);
    }
  }, []);

  const loadStudents = useCallback(async () => {
    if (!agentId) return;
    try {
      const fresh = await desktopApi.getStudents();
      setStudents(fresh);
      setStudentsOffline(false);
      writeStudentsCache(agentId, fresh);
    } catch (error) {
      setStudentsOffline(true);
    }
  }, [agentId]);

  useEffect(() => {
    loadConsignes();
    loadStudents();
  }, [loadConsignes, loadStudents]);

  const sections = useMemo(() => (data?.sections || []), [data]);
  const section = sections.find((item) => item.id === sectionId) || null;
  const studentList = useMemo(() => students?.students || [], [students]);

  const consigneHits = useMemo(() => searchConsignes(sections, query), [sections, query]);
  const studentHits = useMemo(
    () => (query.trim().length >= 2 ? searchStudents(studentList, query).slice(0, 20) : []),
    [studentList, query]
  );
  const filteredStudents = useMemo(() => searchStudents(studentList, studentQuery), [studentList, studentQuery]);

  useEffect(() => {
    if (!focusKey) return undefined;
    const timer = window.setTimeout(() => {
      const target = document.querySelector(`[data-ckey="${focusKey}"]`);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    const clear = window.setTimeout(() => setFocusKey(''), 4000);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clear);
    };
  }, [focusKey, sectionId]);

  const articleKey = (id, title) => `${id}|${title}`;
  const isOpen = (title) => openArticles.has(articleKey(sectionId, title));
  const toggle = (title) => setOpenArticles((previous) => {
    const next = new Set(previous);
    const key = articleKey(sectionId, title);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  const openHit = (hit) => {
    if (hit.article) setOpenArticles((previous) => new Set(previous).add(articleKey(hit.sectionId, hit.article)));
    const highlight = query;
    setQuery('');
    setFocusKey(hit.key);
    navigate(`/consignes/${hit.sectionId}`);
    setSectionQuery(highlight);
  };

  const titles = section ? articleTitles(section.body) : [];
  const allOpen = titles.length > 0 && titles.every((title) => isOpen(title));
  const setAll = (open) => setOpenArticles((previous) => {
    const next = new Set(previous);
    titles.forEach((title) => {
      const key = articleKey(sectionId, title);
      if (open) next.add(key);
      else next.delete(key);
    });
    return next;
  });

  const offlineNote = (when) => (
    <p className="mb-4 flex items-center gap-2 rounded-md border border-border bg-zinc-900/60 px-3 py-2 text-sm text-muted-foreground" data-testid="consignes-offline">
      <WifiOff className="h-4 w-4 shrink-0" />
      Hors réseau : version enregistrée sur cet appareil{when ? ` le ${stamp(when)}` : ''}.
    </p>
  );

  const startStudentEdit = (mode) => {
    const blocked = studentEditBlocked({ online: typeof navigator === 'undefined' || navigator.onLine !== false, listOffline: studentsOffline });
    if (blocked) {
      toast.error(blocked);
      return;
    }
    setStudentEdit(mode);
  };
  const saveStudent = async (action, success, values) => {
    if (values) {
      const invalid = studentFormError(values);
      if (invalid) {
        toast.error(invalid);
        return null;
      }
    }
    const blocked = studentEditBlocked({ online: typeof navigator === 'undefined' || navigator.onLine !== false, listOffline: false });
    if (blocked) {
      toast.error(blocked);
      return null;
    }
    setStudentBusy(true);
    try {
      const result = await action(values ? studentPayload(values) : undefined);
      toast.success(success);
      setStudentEdit('');
      await loadStudents(); // rafraîchit aussi la copie hors ligne
      return result || {};
    } catch (error) {
      toast.error(studentEditError(error));
      return null;
    } finally {
      setStudentBusy(false);
    }
  };

  const openStudent = (student) => {
    setStudentEdit('');
    setNotesTarget({ studentId: student.id });
    if (sectionId !== STUDENTS_ID) {
      setQuery('');
      navigate(`/consignes/${STUDENTS_ID}`);
    }
    window.scrollTo(0, 0);
  };
  const openRoom = (room) => {
    setNotesTarget({ room: String(room).trim() });
    window.scrollTo(0, 0);
  };
  const selectedStudent = notesTarget?.studentId ? studentList.find((item) => item.id === notesTarget.studentId) : null;
  const roomQuery = /^[0-9A-Za-z]{1,6}$/.test(studentQuery.trim()) && /\d/.test(studentQuery) ? studentQuery.trim() : '';

  const searching = query.trim().length >= 2;
  const inStudents = sectionId === STUDENTS_ID;

  return (
    <div className="min-h-screen bg-background pt-[calc(1rem+var(--sat))] pl-[calc(1rem+var(--sal))] pr-[calc(1rem+var(--sar))] pb-[calc(10.5rem+env(safe-area-inset-bottom,0px))] md:pt-[calc(2.5rem+var(--sat))] md:pl-[calc(2.5rem+var(--sal))] md:pr-[calc(2.5rem+var(--sar))] md:pb-16">
      <div className="mx-auto max-w-3xl">
        <button
          type="button"
          onClick={() => (sectionId ? navigate('/consignes') : navigate('/dashboard'))}
          data-testid="consignes-back"
          className="mb-3 flex min-h-11 items-center gap-2 text-muted-foreground transition-colors hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          {sectionId ? 'Toutes les consignes' : 'Retour à l\'accueil'}
        </button>
        <h1 className="mb-4 text-3xl font-bold uppercase tracking-tight md:text-5xl" data-testid="consignes-title">
          {inStudents ? 'Étudiants' : section ? section.title : 'Consignes'}
        </h1>

        {!inStudents && (
          <div className="sticky top-[var(--sat)] z-20 -mx-1 mb-5 bg-background/95 px-1 py-2 backdrop-blur">
            <SearchField
              value={query}
              onChange={setQuery}
              placeholder="Rechercher (ex. buanderie, 05h30, caution)"
              label="Rechercher dans les consignes et les étudiants"
              testId="consignes-search"
            />
          </div>
        )}

        {offline && !inStudents && offlineNote(data?.saved_at)}
        {!data && !offline && <p className="text-muted-foreground">Chargement…</p>}
        {!data && offline && <p className="text-muted-foreground">Les consignes n'ont pas encore été enregistrées sur cet appareil. Ouvrez cet onglet une fois avec du réseau.</p>}

        {searching && !inStudents && (
          <div data-testid="consignes-results" className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {consigneHits.length + studentHits.length === 0
                ? 'Aucun résultat.'
                : `${consigneHits.length + studentHits.length} résultat(s)`}
            </p>
            {studentHits.length > 0 && (
              <div>
                <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-primary">Étudiants</h2>
                <ul className="space-y-2">
                  {studentHits.map((student) => <StudentRow key={student.id || `${student.room}-${student.last_name}`} student={student} query={query} onOpen={openStudent} />)}
                </ul>
              </div>
            )}
            {consigneHits.length > 0 && (
              <ul className="space-y-2">
                {consigneHits.map((hit) => (
                  <li key={`${hit.sectionId}-${hit.key}`}>
                    <button
                      type="button"
                      onClick={() => openHit(hit)}
                      data-testid="consignes-hit"
                      className="flex min-h-16 w-full items-start gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left hover:border-primary/60"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold uppercase tracking-wide text-primary">
                          {hit.sectionTitle}{hit.article ? ` · ${hit.article}` : hit.heading ? ` · ${hit.heading}` : ''}
                        </p>
                        <p className="mt-1 text-[16px] leading-snug"><Highlight text={hit.snippet} query={query} /></p>
                      </div>
                      <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {!searching && !sectionId && data && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="consignes-home">
            {sections.map((item) => {
              const info = CARD_INFO[item.id] || { icon: ClipboardList, hint: '' };
              const Icon = info.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => navigate(`/consignes/${item.id}`)}
                  data-testid={`consignes-card-${item.id}`}
                  className={`flex min-h-20 items-center gap-4 rounded-lg border p-4 text-left transition-colors ${info.highlight
                    ? 'border-amber-500/70 bg-amber-500/10 hover:bg-amber-500/15 sm:col-span-2'
                    : 'border-border bg-card hover:border-primary/60'}`}
                >
                  <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${info.highlight ? 'bg-amber-500/20 text-amber-300' : 'bg-primary/15 text-primary'}`}>
                    <Icon className="h-6 w-6" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[17px] font-bold leading-tight">{item.title}</span>
                    {info.hint && <span className="mt-1 block text-sm text-muted-foreground">{info.hint}</span>}
                  </span>
                  <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => navigate(`/consignes/${STUDENTS_ID}`)}
              data-testid="consignes-card-etudiants"
              className="flex min-h-20 items-center gap-4 rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-primary/60"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <Users className="h-6 w-6" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[17px] font-bold leading-tight">Étudiants</span>
                <span className="mt-1 block text-sm text-muted-foreground">
                  {studentList.length ? `${studentList.length} étudiant(s), recherche par nom ou chambre` : 'Liste pas encore importée'}
                </span>
              </span>
              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
            </button>
          </div>
        )}

        {!searching && section && (
          <article data-testid={`consignes-section-${section.id}`}>
            {titles.length > 1 && (
              <div className="mb-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => setAll(!allOpen)}
                  className="min-h-11 rounded-md px-3 text-sm font-semibold text-primary hover:bg-primary/10"
                  data-testid="consignes-toggle-all"
                >
                  {allOpen ? 'Tout fermer' : 'Tout ouvrir'}
                </button>
              </div>
            )}
            <ConsignesMarkdown
              body={section.body}
              sectionId={section.id}
              query={sectionQuery}
              focusKey={focusKey}
              isOpen={isOpen}
              onToggle={toggle}
            />
            {section.updated_at && (
              <p className="mt-8 text-xs text-muted-foreground">Mis à jour le {stamp(section.updated_at)}</p>
            )}
          </article>
        )}

        {!searching && sectionId && !section && !inStudents && data && (
          <p className="text-muted-foreground">Section introuvable.</p>
        )}

        {inStudents && selectedStudent && (
          <div className="mb-4 space-y-3" data-testid="student-actions">
            {studentEdit === 'edit' ? (
              <StudentForm
                key={selectedStudent.id}
                initial={selectedStudent}
                busy={studentBusy}
                label="Enregistrer la fiche"
                testId="agent-student-form"
                onCancel={() => setStudentEdit('')}
                onSubmit={(values) => saveStudent((payload) => desktopApi.agentUpdateStudent(selectedStudent.id, payload), 'Fiche modifiée', values)}
              />
            ) : studentEdit === 'archive' ? (
              <div className="rounded-lg border border-red-500/50 bg-card p-4" data-testid="student-archive-confirm-box">
                <p className="mb-3 text-[15px]">
                  Retirer cet étudiant de la liste ? Sa fiche et ses notes sont gardées (archivées) et restent dans l'historique de la chambre.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    disabled={studentBusy}
                    onClick={async () => {
                      if (await saveStudent(() => desktopApi.agentArchiveStudent(selectedStudent.id), 'Étudiant retiré de la liste')) setNotesTarget(null);
                    }}
                    className="h-12 rounded-md bg-red-600 px-5 font-bold text-white disabled:opacity-60"
                    data-testid="agent-student-archive-confirm"
                  >
                    Confirmer le retrait
                  </button>
                  <button type="button" onClick={() => setStudentEdit('')} className="h-12 rounded-md border border-border px-5">Garder</button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => startStudentEdit('edit')}
                  className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-lg border border-border px-4 text-[15px] font-semibold hover:border-primary/60"
                  data-testid="agent-student-edit"
                >
                  <Pencil className="h-5 w-5" /> Modifier / Changer de chambre
                </button>
                <button
                  type="button"
                  onClick={() => startStudentEdit('archive')}
                  className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-lg border border-border px-4 text-[15px] font-semibold text-red-300 hover:border-red-500/60"
                  data-testid="agent-student-archive"
                >
                  <Archive className="h-5 w-5" /> Retirer de la liste
                </button>
              </div>
            )}
          </div>
        )}

        {inStudents && notesTarget && (selectedStudent || notesTarget.room) && (
          <FollowupNotes
            student={selectedStudent}
            room={selectedStudent ? '' : notesTarget.room}
            currentAgent={currentAgent}
            onBack={() => setNotesTarget(null)}
            onChanged={loadStudents}
            onOpenRoom={openRoom}
          />
        )}

        {inStudents && !(notesTarget && (selectedStudent || notesTarget.room)) && (
          <section data-testid="students-section">
            <div className="sticky top-[var(--sat)] z-20 -mx-1 mb-4 bg-background/95 px-1 py-2 backdrop-blur">
              <SearchField
                value={studentQuery}
                onChange={setStudentQuery}
                placeholder="Nom, prénom ou chambre"
                label="Rechercher un étudiant par nom ou chambre"
                testId="students-search"
              />
            </div>
            {studentsOffline && offlineNote(students?.saved_at)}
            {studentEdit === 'add' ? (
              <div className="mb-4">
                <StudentForm
                  busy={studentBusy}
                  label="Ajouter l'étudiant"
                  testId="agent-student-form"
                  onCancel={() => setStudentEdit('')}
                  onSubmit={async (values) => {
                    const created = await saveStudent((payload) => desktopApi.agentCreateStudent(payload), 'Étudiant ajouté', values);
                    if (created?.id) setNotesTarget({ studentId: created.id });
                  }}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => startStudentEdit('add')}
                className="mb-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-primary/50 px-4 text-[15px] font-semibold text-primary hover:bg-primary/10"
                data-testid="agent-student-add"
              >
                <UserPlus className="h-5 w-5" /> Ajouter un étudiant
              </button>
            )}
            {!students && !studentsOffline && <p className="text-muted-foreground">Chargement…</p>}
            {students && studentList.length === 0 && (
              <p className="rounded-lg border border-border bg-card p-4 text-muted-foreground" data-testid="students-empty">
                La liste des étudiants est vide. Ajoutez un étudiant ci-dessus, ou l'administrateur importe la liste dans Administration, Liste des étudiants.
              </p>
            )}
            {studentList.length > 0 && (
              <>
                <p className="mb-3 text-sm text-muted-foreground" data-testid="students-count">
                  {studentQuery ? `${filteredStudents.length} sur ${studentList.length} étudiant(s)` : `${studentList.length} étudiant(s)`}
                  {students?.source_label ? ` · ${students.source_label}` : students?.updated_at ? ` · liste du ${stamp(students.updated_at)}` : ''}
                </p>
                <Legend />
                {roomQuery && (
                  <button
                    type="button"
                    onClick={() => openRoom(roomQuery)}
                    data-testid="room-notes-open"
                    className="mb-3 flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-primary/50 px-4 text-[15px] font-semibold text-primary hover:bg-primary/10"
                  >
                    <DoorOpen className="h-5 w-5" /> Notes de la chambre {roomQuery}
                  </button>
                )}
                <ul className="space-y-2">
                  {filteredStudents.map((student) => (
                    <StudentRow key={student.id || `${student.room}-${student.last_name}`} student={student} query={studentQuery} onOpen={openStudent} />
                  ))}
                </ul>
                {filteredStudents.length === 0 && <p className="text-muted-foreground">Aucun étudiant trouvé.</p>}
              </>
            )}
            <p className="mt-6 text-xs text-muted-foreground">
              Liste réservée aux agents connectés. Elle est effacée de cet appareil à la déconnexion.
              Touchez un étudiant pour voir ou ajouter des notes de suivi, modifier sa fiche ou le retirer de la liste.
            </p>
          </section>
        )}
      </div>
    </div>
  );
};

export default ConsignesPage;
